import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  Divider,
  Grid,
  Group,
  Modal,
  Paper,
  ScrollArea,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconExternalLink, IconRestore, IconSearch, IconTrash, IconX } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiClientError } from "../api/client";
import { getSavedSimsLimit } from "./Settings";
import { EmptyState, ErrorState, LoadingState } from "../components/ui";
import { monoStyle } from "../theme";

interface FinalistRow {
  combo_index: number;
  settings: Record<string, unknown>;
  combination_variations: number;
  wins: number;
  losses: number;
  take_profits: number;
  stop_losses: number;
  total_losses: number;
  take_profit_total_won_usdc: number;
  stop_loss_total_lost_usdc: number;
  no_fill_wins: number;
  no_fill_losses: number;
  skips: number;
  blocked: number;
  no_bet: number;
  unresolved: number;
  trades: number;
  win_rate: number;
  est_pnl_usdc: number;
  fees_usdc: number;
  average_odds: number | null;
}

interface SimulationLogRow {
  ts: string;
  level: "info" | "warn" | "error";
  message: string;
}

function statusColor(status: string | null | undefined): string {
  switch (status) {
    case "queued": return "yellow";
    case "running": return "blue";
    case "succeeded": return "green";
    case "failed": return "red";
    case "cancelled": return "gray";
    default: return "gray";
  }
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof ApiClientError) return `${error.code}: ${error.message}`;
  if (error instanceof Error) return error.message;
  return "unknown error";
}

function toLocalDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const dt = new Date(iso);
  if (!Number.isFinite(dt.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

/**
 * Extract the effective `from`/`to` range from a saved run's request
 * payload. Single-mode runs store the range at the top level
 * (`request.from` / `request.to`), while sweep-mode runs (Automated /
 * Modal) carry it nested under `request.sweep.range.{from,to}`. Without
 * this fallback the "Load in Manual Simulator" deep-link from a Modal
 * finalist would inherit whatever range the manual sim panel happened to
 * have, producing very different (broader / shifted) results vs the
 * Modal sweep — the exact apples-to-oranges trap users hit when
 * verifying a Modal top finalist.
 */
function extractRunRange(request: unknown): { from: string; to: string } {
  const req = (request ?? {}) as Record<string, unknown>;
  const topFrom = req.from as string | null | undefined;
  const topTo = req.to as string | null | undefined;
  const sweep = (req.sweep ?? {}) as Record<string, unknown>;
  const range = (sweep.range ?? {}) as Record<string, unknown>;
  const sweepFrom = range.from as string | null | undefined;
  const sweepTo = range.to as string | null | undefined;
  return {
    from: toLocalDateTime(topFrom ?? sweepFrom),
    to: toLocalDateTime(topTo ?? sweepTo),
  };
}

function buildManualSimulatorImportUrl(args: {
  settings: Record<string, unknown>;
  from: string;
  to: string;
}): string {
  const base = `${window.location.origin}${window.location.pathname}`;
  const params = new URLSearchParams({
    import_settings: JSON.stringify(args.settings),
  });
  if (args.from.trim()) params.set("import_from", args.from);
  if (args.to.trim()) params.set("import_to", args.to);
  return `${base}#/analytics/manual-simulator?${params.toString()}`;
}

// Deep-link a saved SWEEP form into the Automated Simulator. The entire
// sweep payload (fixed config + swept axes + range + results / fast-screen
// settings) is JSON-encoded into the hash query; the Automated Simulator
// reconstructs its form state from it on mount (see
// `parseImportedSweepFromUrl` / `reconstructSweepFormState` there). The
// no-fill-subtraction toggle rides alongside since it lives on the run
// request rather than inside the sweep form.
function buildAutomatedSimulatorImportUrl(args: {
  sweep: Record<string, unknown>;
  subtractNoFills: boolean;
  excludeStaleOdds: boolean;
}): string {
  const base = `${window.location.origin}${window.location.pathname}`;
  const params = new URLSearchParams({
    import_sweep: JSON.stringify(args.sweep),
    import_subtract_no_fills: args.subtractNoFills ? "1" : "0",
    import_exclude_stale_odds: args.excludeStaleOdds ? "1" : "0",
  });
  return `${base}#/analytics/automated-simulator?${params.toString()}`;
}

type RunKind = "single" | "local" | "modal";

function runKindOf(run: {
  mode: string;
  result_summary?: Record<string, unknown> | null;
  request?: unknown;
}): RunKind {
  if (run.mode === "single") return "single";
  // Prefer the tier stamped on the final result summary (set on success).
  // Interrupted runs (cancelled/failed) never reach that persist step, so
  // fall back to the tier recorded in the original request payload —
  // otherwise a cancelled Modal sweep is mislabelled "Local".
  const summaryTarget = String(
    (run.result_summary as Record<string, unknown> | null | undefined)?.target ?? "",
  ).toLowerCase();
  const requestTarget = String(
    (run.request as { target?: unknown } | null | undefined)?.target ?? "",
  ).toLowerCase();
  const target = summaryTarget || requestTarget;
  return target === "modal" ? "modal" : "local";
}

const KIND_META: Record<RunKind, { label: string; color: string }> = {
  single: { label: "Single", color: "cyan" },
  local: { label: "Local", color: "violet" },
  modal: { label: "Modal", color: "orange" },
};

export function SavedSimulationsPage() {
  const queryClient = useQueryClient();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [settingsModalRow, setSettingsModalRow] = useState<FinalistRow | null>(null);
  const [kindFilter, setKindFilter] = useState<"all" | RunKind>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "succeeded" | "failed" | "cancelled">("all");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const savedSimsLimit = getSavedSimsLimit();

  const savedQ = useQuery({
    queryKey: ["simulations", "saved", "all", "page", savedSimsLimit],
    queryFn: () => api.simulations.saved({ limit: savedSimsLimit }),
    refetchInterval: 15_000,
  });

  const savedRuns = savedQ.data ?? [];

  useEffect(() => {
    if (savedRuns.length === 0) {
      if (selectedRunId != null) setSelectedRunId(null);
      return;
    }
    if (selectedRunId && savedRuns.some((run) => run.run_id === selectedRunId)) return;
    setSelectedRunId(savedRuns[0]!.run_id);
  }, [selectedRunId, savedRuns]);

  const selectedRunQ = useQuery({
    queryKey: ["simulations", "detail", selectedRunId],
    queryFn: () => api.simulations.get(selectedRunId!),
    enabled: selectedRunId != null,
    staleTime: 30_000,
  });

  const selectedRun = selectedRunQ.data ?? (savedRuns.find((run) => run.run_id === selectedRunId) ?? null);

  // Recover the partial top-K from a failed sweep by spawning
  // `sim-bot aggregate` server-side. Only works on sweeps that wrote
  // batch_result_*.json artifacts (everything after the rollout); the
  // backend returns a clear 400 with the reason otherwise.
  const recoverMut = useMutation({
    mutationFn: (runId: string) => api.simulations.recover(runId),
    onSuccess: (_, runId) => {
      notifications.show({
        title: "Partial results recovered",
        message: `Run ${runId} now contains the top-K rebuilt from on-disk batch artifacts.`,
        color: "green",
      });
      // The recover endpoint rewrites simulation_results and
      // simulation_runs.result_summary_json, so the detail panel
      // needs to refetch.
      queryClient.invalidateQueries({ queryKey: ["simulations", "detail", runId] });
      queryClient.invalidateQueries({ queryKey: ["simulations", "saved"] });
    },
    onError: (error) => {
      notifications.show({
        title: "Could not recover partial results",
        message: extractErrorMessage(error),
        color: "red",
      });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (runId: string) => api.simulations.delete(runId),
    onSuccess: (_, runId) => {
      if (selectedRunId === runId) setSelectedRunId(null);
      savedQ.refetch();
      queryClient.removeQueries({ queryKey: ["simulations", "detail", runId] });
      notifications.show({
        title: "Saved simulation deleted",
        message: `Run ${runId} was deleted.`,
        color: "gray",
      });
    },
    onError: (error) => {
      notifications.show({
        title: "Failed to delete saved simulation",
        message: extractErrorMessage(error),
        color: "red",
      });
    },
  });

  const bulkDeleteMut = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => api.simulations.delete(id)));
      const failed = results
        .map((r, i) => ({ r, id: ids[i]! }))
        .filter((x) => x.r.status === "rejected");
      return { total: ids.length, failed };
    },
    onSuccess: ({ total, failed }, ids) => {
      if (selectedRunId && ids.includes(selectedRunId)) setSelectedRunId(null);
      setSelectedIds(new Set());
      savedQ.refetch();
      for (const id of ids) {
        queryClient.removeQueries({ queryKey: ["simulations", "detail", id] });
      }
      if (failed.length === 0) {
        notifications.show({
          title: "Saved simulations deleted",
          message: `Deleted ${total} run${total === 1 ? "" : "s"}.`,
          color: "gray",
        });
      } else {
        notifications.show({
          title: "Some deletes failed",
          message: `${total - failed.length} of ${total} deleted; ${failed.length} failed.`,
          color: "red",
        });
      }
    },
    onError: (error) => {
      notifications.show({
        title: "Failed to bulk delete",
        message: extractErrorMessage(error),
        color: "red",
      });
    },
  });

  const filteredRuns = useMemo(() => {
    const q = search.trim().toLowerCase();
    return savedRuns.filter((run) => {
      if (statusFilter !== "all" && run.status !== statusFilter) return false;
      if (kindFilter !== "all") {
        const kind = runKindOf(run);
        if (kind !== kindFilter) return false;
      }
      if (q && !run.run_id.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [savedRuns, kindFilter, statusFilter, search]);

  // Prune selection when filtered set changes (drop ids no longer visible).
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const visible = new Set(filteredRuns.map((r) => r.run_id));
    let mutated = false;
    const next = new Set<string>();
    for (const id of selectedIds) {
      if (visible.has(id)) next.add(id);
      else mutated = true;
    }
    if (mutated) setSelectedIds(next);
  }, [filteredRuns, selectedIds]);

  const allFilteredSelected = filteredRuns.length > 0 && filteredRuns.every((r) => selectedIds.has(r.run_id));
  const someFilteredSelected = !allFilteredSelected && filteredRuns.some((r) => selectedIds.has(r.run_id));
  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredRuns.map((r) => r.run_id)));
    }
  };
  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const summary = (selectedRun?.result_summary ?? null) as Record<string, unknown> | null;
  const noFillSubtractionEnabled = Boolean(summary?.subtract_no_fills_from_results);
  const staleOddsExcludedEnabled = Boolean(summary?.exclude_stale_odds);

  const runLogs = useMemo<SimulationLogRow[]>(() => {
    if (!summary || !Array.isArray(summary.logs)) return [];
    return (summary.logs as Array<Record<string, unknown>>)
      .map((row) => {
        const level: SimulationLogRow["level"] = row.level === "warn" || row.level === "error"
          ? row.level
          : "info";
        return {
          ts: String(row.ts ?? ""),
          level,
          message: String(row.message ?? ""),
        };
      })
      .filter((row) => row.ts || row.message)
      .slice(-200);
  }, [summary]);

  const finalists = useMemo<FinalistRow[]>(() => {
    if (!summary || !Array.isArray(summary.finalists)) return [];
    return (summary.finalists as Array<Record<string, unknown>>).map((f) => ({
      combo_index: Number(f.combo_index ?? 0),
      settings: (f.settings as Record<string, unknown>) ?? {},
      combination_variations: Number(f.combination_variations ?? 1),
      wins: Number(f.wins ?? 0),
      losses: Number(f.losses ?? 0),
      take_profits: Number(f.take_profits ?? 0),
      stop_losses: Number(f.stop_losses ?? 0),
      total_losses: Number(f.total_losses ?? (Number(f.losses ?? 0) + Number(f.stop_losses ?? 0))),
      take_profit_total_won_usdc: Number(f.take_profit_total_won_usdc ?? 0),
      stop_loss_total_lost_usdc: Number(f.stop_loss_total_lost_usdc ?? 0),
      no_fill_wins: Number(f.no_fill_wins ?? 0),
      no_fill_losses: Number(f.no_fill_losses ?? 0),
      skips: Number(f.skips ?? 0),
      blocked: Number(f.blocked ?? 0),
      no_bet: Number(f.no_bet ?? 0),
      unresolved: Number(f.unresolved ?? 0),
      trades: Number(f.trades ?? 0),
      win_rate: Number(f.win_rate ?? 0),
      est_pnl_usdc: Number(f.est_pnl_usdc ?? 0),
      fees_usdc: Number(f.fees_usdc ?? 0),
      average_odds: f.average_odds == null ? null : Number(f.average_odds),
    }));
  }, [summary]);

  const topFinalist = finalists[0] ?? null;
  const topResults = Number(summary?.top_k_global ?? finalists.length);

  return (
    <Stack gap="lg">
      <Stack gap={2}>
        <Title order={2}>Saved Simulations</Title>
        <Text c="dimmed" size="sm">
          Browse all completed manual and automated simulation runs, inspect the original config and returned results,
          and delete runs you no longer need.
        </Text>
      </Stack>

      <Grid gutter="lg" maw={1600} w="100%" mx="auto" align="start">
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Card withBorder>
            <Stack gap="sm">
              <Group justify="space-between" align="center">
                <Group gap="xs" align="center">
                  <Title order={5}>Saved Runs</Title>
                  <Badge color="gray" variant="light">
                    {filteredRuns.length}
                    {filteredRuns.length !== savedRuns.length ? ` / ${savedRuns.length}` : ""}
                  </Badge>
                </Group>
                {savedQ.isLoading ? <Badge color="gray">loading</Badge> : null}
              </Group>

              <Stack gap={6}>
                <SegmentedControl
                  size="xs"
                  fullWidth
                  value={kindFilter}
                  onChange={(v) => setKindFilter(v as typeof kindFilter)}
                  data={[
                    { label: "All", value: "all" },
                    { label: "Single", value: "single" },
                    { label: "Local", value: "local" },
                    { label: "Modal", value: "modal" },
                  ]}
                />
                <SegmentedControl
                  size="xs"
                  fullWidth
                  value={statusFilter}
                  onChange={(v) => setStatusFilter(v as typeof statusFilter)}
                  data={[
                    { label: "Any status", value: "all" },
                    { label: "Succeeded", value: "succeeded" },
                    { label: "Failed", value: "failed" },
                    { label: "Cancelled", value: "cancelled" },
                  ]}
                />
                <TextInput
                  size="xs"
                  placeholder="Search by run id"
                  leftSection={<IconSearch size={14} />}
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  rightSection={search ? (
                    <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setSearch("")}>
                      <IconX size={12} />
                    </ActionIcon>
                  ) : null}
                />
              </Stack>

              {savedQ.isLoading ? <LoadingState label="Loading saved simulations..." /> : null}
              {savedQ.isError ? <ErrorState error={savedQ.error as Error} onRetry={() => savedQ.refetch()} /> : null}

              {!savedQ.isLoading && !savedQ.isError && savedRuns.length === 0 ? (
                <EmptyState label="No completed simulation runs found." />
              ) : null}
              {!savedQ.isLoading && !savedQ.isError && savedRuns.length > 0 && filteredRuns.length === 0 ? (
                <EmptyState label="No runs match the current filters." />
              ) : null}

              {filteredRuns.length > 0 ? (
                <>
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Checkbox
                      size="xs"
                      checked={allFilteredSelected}
                      indeterminate={someFilteredSelected}
                      onChange={toggleSelectAll}
                      label={
                        selectedIds.size > 0
                          ? `${selectedIds.size} selected`
                          : `Select all (${filteredRuns.length})`
                      }
                    />
                    {selectedIds.size > 0 ? (
                      <Button
                        size="xs"
                        color="red"
                        variant="light"
                        leftSection={<IconTrash size={14} />}
                        loading={bulkDeleteMut.isPending}
                        onClick={() => {
                          const ids = Array.from(selectedIds);
                          if (window.confirm(`Delete ${ids.length} saved simulation${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) {
                            bulkDeleteMut.mutate(ids);
                          }
                        }}
                      >
                        Delete ({selectedIds.size})
                      </Button>
                    ) : null}
                  </Group>

                  <ScrollArea h={680} type="auto" offsetScrollbars>
                    <Stack gap={6} pr="xs">
                      {filteredRuns.map((run) => {
                        const isSelected = run.run_id === selectedRunId;
                        const isChecked = selectedIds.has(run.run_id);
                        const runSummary = (run.result_summary ?? null) as Record<string, unknown> | null;
                        const finalistsCount = runSummary?.finalists_count != null
                          ? Number(runSummary.finalists_count)
                          : Array.isArray(runSummary?.finalists)
                            ? (runSummary!.finalists as unknown[]).length
                            : null;
                        const kind = runKindOf(run);
                        const kindMeta = KIND_META[kind];
                        const finishedAt = run.finished_at ? new Date(run.finished_at) : null;
                        return (
                          <Paper
                            key={run.run_id}
                            withBorder
                            p="xs"
                            style={{
                              borderColor: isSelected ? "var(--mantine-color-blue-5)" : undefined,
                              borderWidth: isSelected ? 2 : 1,
                              cursor: "pointer",
                              background: isChecked ? "var(--mantine-color-blue-0)" : undefined,
                            }}
                            onClick={() => setSelectedRunId(run.run_id)}
                          >
                            <Group gap="xs" align="center" wrap="nowrap">
                              <Checkbox
                                size="xs"
                                checked={isChecked}
                                onChange={() => toggleOne(run.run_id)}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Select ${run.run_id}`}
                              />
                              <span
                                title={run.status}
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  background: `var(--mantine-color-${statusColor(run.status)}-6)`,
                                  flexShrink: 0,
                                }}
                              />
                              <Badge
                                size="sm"
                                variant="light"
                                color={kindMeta.color}
                                style={{ flexShrink: 0 }}
                              >
                                {kindMeta.label}
                              </Badge>
                              <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                                <Text size="sm" fw={600} style={{ ...monoStyle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {run.run_id}
                                </Text>
                                <Text size="xs" c="dimmed" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {finishedAt ? finishedAt.toLocaleString() : "in progress"}
                                  {finalistsCount != null ? ` · ${finalistsCount} finalist${finalistsCount === 1 ? "" : "s"}` : ""}
                                </Text>
                              </Stack>
                              {isSelected ? (
                                <Badge size="xs" color="blue" variant="filled" style={{ flexShrink: 0 }}>
                                  Viewing
                                </Badge>
                              ) : null}
                            </Group>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </ScrollArea>
                </>
              ) : null}
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Stack gap="md">
            {!selectedRun ? <EmptyState label="Select a saved simulation from the list." /> : null}

            {selectedRun ? (
              <>
                <Card withBorder>
                  <Stack gap="sm">
                    <Group justify="space-between" align="center">
                      <Group gap="xs" align="center">
                        <Title order={4}>Saved Run Details</Title>
                        <Badge color={statusColor(selectedRun.status)}>{selectedRun.status}</Badge>
                      </Group>
                      <Group gap="xs">
                        {selectedRun.mode === "single" && Boolean(selectedRun.request?.config_override) && (
                          <Button
                            size="xs"
                            variant="light"
                            color="blue"
                            leftSection={<IconExternalLink size={14} />}
                            component="a"
                            href={buildManualSimulatorImportUrl({
                              settings: (selectedRun.request?.config_override ?? {}) as Record<string, unknown>,
                              ...extractRunRange(selectedRun.request),
                            })}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Load in Manual Simulator
                          </Button>
                        )}
                        {/*
                          Recover Partial Results: appears for failed
                          or cancelled SWEEP runs only. On the backend
                          this spawns `sim-bot aggregate` against the
                          per-run `batch_result_*.json` artifacts and
                          repopulates `simulation_results`. If no
                          artifacts exist (e.g. a sweep that pre-dates
                          the artifact-write rollout, or a sweep that
                          died before its first batch finished), the
                          endpoint returns a clear 400 which is shown
                          in the error notification.
                        */}
                        {selectedRun.mode === "sweep" && (selectedRun.status === "failed" || selectedRun.status === "cancelled") && (
                          <Button
                            size="xs"
                            variant="light"
                            color="green"
                            leftSection={<IconRestore size={14} />}
                            onClick={() => recoverMut.mutate(selectedRun.run_id)}
                            loading={recoverMut.isPending}
                          >
                            Recover Partial Results
                          </Button>
                        )}
                        {/*
                          Load Config into Automated Simulator: deep-links
                          the saved sweep form (fixed + axes + range +
                          results/fast-screen settings + no-fill flag) into
                          the Automated Simulator form so the operator can
                          review / tweak the exact swept config and launch a
                          fresh run from there. Sweep-mode only; single-mode
                          runs use "Load in Manual Simulator" above.
                        */}
                        {selectedRun.mode === "sweep" && Boolean(selectedRun.request?.sweep) && (
                          <Button
                            size="xs"
                            variant="light"
                            color="blue"
                            leftSection={<IconExternalLink size={14} />}
                            component="a"
                            href={buildAutomatedSimulatorImportUrl({
                              sweep: (selectedRun.request?.sweep ?? {}) as Record<string, unknown>,
                              subtractNoFills: Boolean(
                                (selectedRun.request as Record<string, unknown> | null)?.subtract_no_fills_from_results,
                              ),
                              excludeStaleOdds: Boolean(
                                (selectedRun.request as Record<string, unknown> | null)?.exclude_stale_odds,
                              ),
                            })}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Load Config into Automated Simulator
                          </Button>
                        )}
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          leftSection={<IconTrash size={14} />}
                          onClick={() => {
                            if (window.confirm(`Delete saved simulation ${selectedRun.run_id}?`)) {
                              deleteMut.mutate(selectedRun.run_id);
                            }
                          }}
                          loading={deleteMut.isPending}
                        >
                          Delete Simulation
                        </Button>
                      </Group>
                    </Group>

                    <Text size="sm">Run ID: <Code style={monoStyle}>{selectedRun.run_id}</Code></Text>
                    <Text size="sm" c="dimmed">
                      Created: {new Date(selectedRun.created_at).toLocaleString()}
                      {selectedRun.started_at ? ` · Started: ${new Date(selectedRun.started_at).toLocaleString()}` : ""}
                      {selectedRun.finished_at ? ` · Finished: ${new Date(selectedRun.finished_at).toLocaleString()}` : ""}
                    </Text>
                    <Badge color={noFillSubtractionEnabled ? "teal" : "gray"} variant="light">
                      No-Fill Subtraction: {noFillSubtractionEnabled ? "ON" : "OFF"}
                    </Badge>
                    <Badge color={staleOddsExcludedEnabled ? "teal" : "gray"} variant="light">
                      Stale Excluded: {staleOddsExcludedEnabled ? "ON" : "OFF"}
                    </Badge>
                    {selectedRun.error ? <Text size="sm" c="red">{selectedRun.error}</Text> : null}
                  </Stack>
                </Card>

                <Card withBorder>
                  <Stack gap="sm">
                    <Title order={5}>Saved Config / Request</Title>
                    <Paper withBorder p="sm" radius="md">
                      <ScrollArea h={220} type="auto" offsetScrollbars>
                        <Code block style={{ ...monoStyle, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {JSON.stringify(selectedRun.request ?? { note: "no request payload stored" }, null, 2)}
                        </Code>
                      </ScrollArea>
                    </Paper>
                  </Stack>
                </Card>

                <Card withBorder>
                  <Stack gap="md">
                    <Title order={4}>Top {topResults} Results</Title>

                    {summary ? (
                      <>
                        <SimpleGrid cols={{ base: 2, md: 4 }}>
                          <Stat label="Combos Evaluated" value={String(summary.combos_evaluated ?? "—")} />
                          <Stat label="Combos With Trades" value={String(summary.combos_valid ?? "—")} />
                          <Stat label="Finalists" value={String(summary.top_k_global ?? finalists.length)} />
                          <Stat
                            label="Top Take Profits"
                            value={topFinalist ? String(topFinalist.take_profits) : "—"}
                            subValue={topFinalist ? `$${topFinalist.take_profit_total_won_usdc.toFixed(2)} won` : undefined}
                            accent="green"
                            subAccent="green"
                          />
                          <Stat
                            label="Top Stop Losses"
                            value={topFinalist ? String(topFinalist.stop_losses) : "—"}
                            subValue={topFinalist ? `$${topFinalist.stop_loss_total_lost_usdc.toFixed(2)} lost` : undefined}
                            accent="red"
                            subAccent="red"
                          />
                          <Stat
                            label="Top No-Fill Wins / Losses"
                            value={topFinalist ? `${topFinalist.no_fill_wins} / ${topFinalist.no_fill_losses}` : "—"}
                          />
                          <Stat
                            label="Best P/L"
                            value={summary.best_pnl_usdc == null ? "—" : `$${Number(summary.best_pnl_usdc).toFixed(2)}`}
                            accent={Number(summary.best_pnl_usdc ?? 0) >= 0 ? "green" : "red"}
                          />
                        </SimpleGrid>

                        <Divider />

                        {finalists.length === 0 ? (
                          <EmptyState label="This saved run has no finalists." />
                        ) : (
                          <ScrollArea h={560} type="auto" offsetScrollbars>
                            <Table striped highlightOnHover withTableBorder stickyHeader>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>#</Table.Th>
                                  <Table.Th>Settings</Table.Th>
                                  <Table.Th>Wins</Table.Th>
                                  <Table.Th>Losses (Incl. SL)</Table.Th>
                                  <Table.Th>Stop Losses</Table.Th>
                                  <Table.Th>No-Fill W / L</Table.Th>
                                  <Table.Th>Win Rate</Table.Th>
                                  <Table.Th>P/L</Table.Th>
                                  <Table.Th>Fees</Table.Th>
                                  <Table.Th>Combination Variations</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {finalists.map((row, idx) => (
                                  <Table.Tr key={`${row.combo_index}-${idx}`}>
                                    <Table.Td>{idx + 1}</Table.Td>
                                    <Table.Td>
                                      <Anchor
                                        component="button"
                                        type="button"
                                        size="sm"
                                        onClick={() => setSettingsModalRow(row)}
                                      >
                                        View Settings
                                      </Anchor>
                                    </Table.Td>
                                    <Table.Td>{row.wins}</Table.Td>
                                    <Table.Td>{row.total_losses}</Table.Td>
                                    <Table.Td>
                                      <Text fw={800} style={monoStyle}>{row.stop_losses}</Text>
                                      <Text size="xs" c="red" style={monoStyle}>${row.stop_loss_total_lost_usdc.toFixed(2)} lost</Text>
                                    </Table.Td>
                                    <Table.Td>{row.no_fill_wins} / {row.no_fill_losses}</Table.Td>
                                    <Table.Td>{(row.win_rate * 100).toFixed(1)}%</Table.Td>
                                    <Table.Td style={{ color: row.est_pnl_usdc >= 0 ? "var(--mantine-color-green-6)" : "var(--mantine-color-red-6)" }}>
                                      {row.est_pnl_usdc >= 0 ? "+" : ""}${row.est_pnl_usdc.toFixed(2)}
                                    </Table.Td>
                                    <Table.Td>${row.fees_usdc.toFixed(2)}</Table.Td>
                                    <Table.Td>{row.combination_variations}</Table.Td>
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>
                          </ScrollArea>
                        )}
                      </>
                    ) : (
                      <EmptyState label="No saved result summary found for this run." />
                    )}
                  </Stack>
                </Card>

                <Card withBorder>
                  <Stack gap="xs">
                    <Title order={5}>Simulation Log</Title>
                    {runLogs.length === 0 ? (
                      <Text size="sm" c="dimmed">No log lines stored for this run.</Text>
                    ) : (
                      <ScrollArea h={260} type="auto" offsetScrollbars>
                        <Stack gap={4}>
                          {runLogs.map((row, idx) => (
                            <Text
                              key={`${row.ts}-${idx}`}
                              size="xs"
                              style={{
                                ...monoStyle,
                                color: row.level === "error"
                                  ? "var(--mantine-color-red-6)"
                                  : row.level === "warn"
                                    ? "var(--mantine-color-yellow-7)"
                                    : "inherit",
                              }}
                            >
                              {row.ts ? `[${row.ts}] ` : ""}
                              {row.level.toUpperCase()} {row.message}
                            </Text>
                          ))}
                        </Stack>
                      </ScrollArea>
                    )}
                  </Stack>
                </Card>
              </>
            ) : null}
          </Stack>
        </Grid.Col>
      </Grid>

      <Modal
        opened={settingsModalRow != null}
        onClose={() => setSettingsModalRow(null)}
        title={settingsModalRow ? `Combo #${settingsModalRow.combo_index} Settings` : "Settings"}
        size="lg"
      >
        {settingsModalRow ? (
          <Stack gap="sm">
            <Button
              size="sm"
              variant="light"
              color="blue"
              leftSection={<IconExternalLink size={14} />}
              component="a"
              href={buildManualSimulatorImportUrl({
                settings: settingsModalRow.settings,
                ...extractRunRange(selectedRun?.request),
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
              Load in Manual Simulator
            </Button>
            <Paper withBorder p="sm" radius="md">
              <ScrollArea h={420} type="auto" offsetScrollbars>
                <Code block style={{ ...monoStyle, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {JSON.stringify(settingsModalRow.settings, null, 2)}
                </Code>
              </ScrollArea>
            </Paper>
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}

function Stat({
  label,
  value,
  accent,
  subValue,
  subAccent,
}: {
  label: string;
  value: string;
  accent?: "green" | "red";
  subValue?: string;
  subAccent?: "green" | "red";
}) {
  const color = accent === "green"
    ? "var(--mantine-color-green-6)"
    : accent === "red"
      ? "var(--mantine-color-red-6)"
      : undefined;
  const subColor = subAccent === "green"
    ? "var(--mantine-color-green-6)"
    : subAccent === "red"
      ? "var(--mantine-color-red-6)"
      : "var(--mantine-color-dimmed)";
  return (
    <Paper withBorder radius="md" p="sm">
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>{label}</Text>
      <Text fw={800} style={monoStyle} c={color}>{value}</Text>
      {subValue ? <Text size="xs" c={subColor} style={monoStyle}>{subValue}</Text> : null}
    </Paper>
  );
}
